import { Button } from "@ui/Button";
import { Stack } from "@ui/Stack";
import { Heading } from "@ui/typography/Heading";
import { useSearch } from "@utils/usePagination";
import { onCleanup, onMount } from "solid-js";
import { ArtifactKindsProvider } from "~/modules/askgvt/artifacts/utils/ArtifactKindsContext";
import { useArtifactQuery } from "~/modules/askgvt/artifacts/utils/useArtifactQuery";
import { AnswerPanel } from "~/modules/askgvt/studio/question/components/AnswerPanel";
import {
	ASSET_STATE_UPDATED_EVENT,
	type AssetStateUpdatedEventDetail,
} from "~/modules/shared/chat/chat-panel/components/AssetState";
import {
	QUESTION_ANSWER_COMPLETE_EVENT,
	type QuestionAnswerCompleteEventDetail,
} from "~/modules/shared/chat/constants";

type Props = {
	/** Every question in the thread, root first. */
	questionIds: string[];
	onClose: () => void;
};

/** Below this the pane covers the thread (AskPageSidePane.css). */
const PANE_COVERS_THREAD_QUERY = "(width <= 50rem)";

/** The answers saved from this thread, in the AskPage's side pane. */
export const AskPageAnswersPane = (props: Props) => {
	const searchProps = useSearch({});
	const {
		artifactList,
		toggleFavourite,
		isMetadataPending,
		refetchArtifacts,
		refetchAndTrackNewSave,
		refetchUntilNewItems,
	} = useArtifactQuery(() => ({
		question_ids: props.questionIds,
		// Leaves out a creator's own Studio answers, as the Ask answers list does.
		exclude_studio: true,
		limit: 200,
		query: searchProps.search(),
	}));

	const inThread = (questionId: string | undefined) =>
		questionId !== undefined && props.questionIds.includes(questionId);

	onMount(() => {
		const onAssetStateUpdated = (event: Event) => {
			const { detail } = event as CustomEvent<AssetStateUpdatedEventDetail>;
			if (!inThread(detail?.questionId)) {
				return;
			}
			void (detail.saved ? refetchAndTrackNewSave() : refetchArtifacts());
		};
		const onAnswerComplete = (event: Event) => {
			const { detail } =
				event as CustomEvent<QuestionAnswerCompleteEventDetail>;
			if (inThread(detail?.questionId)) {
				void refetchUntilNewItems();
			}
		};
		document.addEventListener(ASSET_STATE_UPDATED_EVENT, onAssetStateUpdated);
		document.addEventListener(QUESTION_ANSWER_COMPLETE_EVENT, onAnswerComplete);
		onCleanup(() => {
			document.removeEventListener(
				ASSET_STATE_UPDATED_EVENT,
				onAssetStateUpdated,
			);
			document.removeEventListener(
				QUESTION_ANSWER_COMPLETE_EVENT,
				onAnswerComplete,
			);
		});
	});

	return (
		<Stack class="ask-page-answers-pane" spacing="medium">
			<Stack direction="row" align="center" justify="space-between">
				<Heading as="h3">Answers</Heading>
				<Button
					variant="text"
					symbol="close"
					aria-label="Close answers"
					onClick={props.onClose}
				/>
			</Stack>
			<ArtifactKindsProvider>
				<AnswerPanel
					answers={artifactList}
					toggleFavourite={toggleFavourite}
					isMetadataPending={isMetadataPending}
					searchProps={searchProps}
					onAnswerSelected={() => {
						// Otherwise the chat scrolls to the answer out of sight, under the pane.
						if (window.matchMedia(PANE_COVERS_THREAD_QUERY).matches) {
							props.onClose();
						}
					}}
				/>
			</ArtifactKindsProvider>
		</Stack>
	);
};
